// File: CustomerBanner.tsx
import React, { useState, useEffect } from 'react';
import { Alert, IconButton, Box, Typography } from '@mui/material';
import { Close as CloseIcon, Business as BusinessIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('CustomerBanner');

const CustomerBanner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use state to track customer view changes
  const [customerViewState, setCustomerViewState] = useState<{ customerId: number; customerName: string } | null>(null);
  
  // Server-side approach: Check for login-as token and retrieve from server
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loginAsToken = params.get('lat');
    
    logger.debug('URL params:', location.search);
    logger.debug('Login token from URL:', loginAsToken);
    
    if (loginAsToken) {
      // Retrieve customer data from server using token
      const fetchCustomerData = async () => {
        logger.debug('Fetching customer data with token:', loginAsToken);
        logger.debug('Full URL:', `/endpoints/login_as_customer?token=${loginAsToken}`);
        try {
          const response = await api.get(`/endpoints/login_as_customer?token=${loginAsToken}`);
          logger.debug('Full server response:', response);
          logger.debug('Response status:', response.status);
          logger.debug('Response data:', response.data);
          logger.debug('Response data.success:', response.data?.success);
          logger.debug('Response data.data:', response.data?.data);
          
          if (response.data.success && response.data.data) {
            const customerData = response.data.data;
            logger.debug('Setting customer data:', customerData);
            
            // Set sessionStorage for this tab
            sessionStorage.setItem('loginAsCustomer', JSON.stringify(customerData));
            setCustomerViewState(customerData);
            
            // Update tab title
            document.title = `${customerData.customerId} - ${customerData.customerName} - Admin View`;
            
            // Dispatch event to notify other components
            window.dispatchEvent(new CustomEvent('loginAsCustomerChanged', { 
              detail: customerData 
            }));
            
            // Clean up URL
            const newUrl = location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          } else {
            logger.warning('No customer data in response');
            logger.debug('Response was:', response.data);
          }
        } catch (error: any) {
          logger.error('Error retrieving login-as data:', error);
          logger.error('Error response:', error.response?.data);
          logger.error('Error status:', error.response?.status);
        }
      };
      
      fetchCustomerData();
    } else {
      // Check if we already have loginAsCustomer in sessionStorage
      const sessionData = sessionStorage.getItem('loginAsCustomer');
      if (sessionData) {
        try {
          const data = JSON.parse(sessionData);
          setCustomerViewState(data);
          // Keep tab title updated with customer ID and name
          document.title = `${data.customerId} - ${data.customerName} - Admin View`;
        } catch (error) {
          logger.error('Error parsing session data:', error);
        }
      }
      
      // Clear on customers page
      if (location.pathname === '/customers') {
        sessionStorage.removeItem('loginAsCustomer');
        setCustomerViewState(null);
        document.title = 'Portal';
      }
    }
  }, [location]);
  
  // Parse URL params for regular customer filter
  const params = new URLSearchParams(location.search);
  const urlCustomerId = params.get('customerId');
  const urlCustomerName = params.get('customerName');
  
  // Determine which mode we're in
  const isLoginAsMode = !!customerViewState;
  const effectiveCustomerId = customerViewState?.customerId || urlCustomerId;
  const customerName = customerViewState?.customerName || urlCustomerName;
  
  logger.debug('State check:', {
    customerViewState,
    urlCustomerId,
    urlCustomerName,
    isLoginAsMode,
    effectiveCustomerId,
    customerName
  });
  
  if (!effectiveCustomerId) {
    return null;
  }
  
  const handleClose = () => {
    if (isLoginAsMode) {
      // For login as mode, clear sessionStorage (tab-specific)
      sessionStorage.removeItem('loginAsCustomer');
      setCustomerViewState(null);
      // Restore original title
      document.title = 'Portal';
      if (window.opener) {
        window.close();
      } else {
        navigate('/customers');
      }
    } else {
      // For regular filter mode, remove customer params from URL
      const newParams = new URLSearchParams(location.search);
      newParams.delete('customerId');
      newParams.delete('customerName');
      const newSearch = newParams.toString();
      navigate({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      });
    }
  };
  
  return (
    <Alert 
      severity={isLoginAsMode ? "warning" : "info"} 
      icon={isLoginAsMode ? <VisibilityIcon /> : <BusinessIcon />}
      action={
        <IconButton
          aria-label="close"
          color="inherit"
          size="small"
          onClick={handleClose}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      }
      sx={{
        mb: 2,
        '& .MuiAlert-message': {
          width: '100%'
        },
        ...(isLoginAsMode && {
          backgroundColor: '#fff3cd',
          color: '#856404',
          '& .MuiAlert-icon': {
            color: '#856404'
          }
        })
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {isLoginAsMode ? 'Viewing As Customer' : 'Managing Customer'} #{effectiveCustomerId}
          </Typography>
          <Typography variant="body2">
            {customerName || 'Unknown Customer'}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
          {isLoginAsMode 
            ? 'You are viewing the portal as this customer. Changes will affect their account.'
            : 'All data shown is filtered for this customer only'}
        </Typography>
      </Box>
    </Alert>
  );
};

export default CustomerBanner;